(() => {
  if (window.sqProductReady) return;
  window.sqProductReady = true;
  function init(root) {
    if (root.dataset.ready) return;
    const form = root.querySelector('.sooqja-product__form');
    const gallery = root.querySelector('[data-product-gallery]');
    if (!form || !gallery) return;
    root.dataset.ready = '1';
    const lifecycle = new AbortController();
    const on = (element, event, listener, options = {}) => element?.addEventListener(event, listener, { ...options, signal: lifecycle.signal });
    const slides = [...gallery.children];
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const previous = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const counter = root.querySelector('[data-gallery-count]');
    let active = 0;
    function setCount(index) {
      active = Math.max(0, Math.min(index, slides.length - 1));
      if (counter) counter.textContent = `${active + 1} / ${slides.length}`;
      thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', String(position === active)));
      if (previous) previous.disabled = active === 0;
      if (next) next.disabled = active === slides.length - 1;
      slides.forEach((slide, position) => {
        const inactive = position !== active;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
        if (inactive) slide.querySelectorAll('video').forEach(video => video.pause());
      });
    }
    function showSlide(index) {
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!slide) return;
      const bounds = gallery.getBoundingClientRect();
      const target = slide.getBoundingClientRect();
      gallery.scrollBy({ left: target.left - bounds.left, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
      setCount(slides.indexOf(slide));
    }
    on(previous, 'click', () => showSlide(active - 1));
    on(next, 'click', () => showSlide(active + 1));
    thumbs.forEach(thumb => on(thumb, 'click', () => showSlide(Number(thumb.dataset.galleryThumb))));
    on(gallery, 'keydown', event => {
      if (event.target !== gallery && !event.target.closest('[data-sq-zoom]')) return;
      const rtl = getComputedStyle(gallery).direction === 'rtl';
      const directions = { ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1 };
      if (!(event.key in directions) && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : active + directions[event.key];
      showSlide(index);
      gallery.focus({ preventScroll: true });
    });
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setCount(slides.indexOf(visible.target));
      }, { root: gallery, threshold: 0.7 });
      slides.forEach(slide => observer.observe(slide));
    } else {
      let queued = false;
      on(gallery, 'scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          const left = gallery.getBoundingClientRect().left;
          const closest = slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - left) < best.distance ? { index, distance: Math.abs(slide.getBoundingClientRect().left - left) } : best, { index: active, distance: Infinity });
          setCount(closest.index);
        });
      });
    }
    setCount(0);
    on(document, 'shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      observer?.disconnect();
      lifecycle.abort();
      delete root.dataset.ready;
    });

    const qty = root.querySelector('[data-qty-input]');
    const minus = root.querySelector('[data-qty-minus]');
    function setQty(value, announce = true) {
      if (!qty) return;
      const minimum = Number(qty.min) || 1;
      const maximum = Number(qty.max) || Infinity;
      qty.value = Math.min(maximum, Math.max(minimum, parseInt(value, 10) || minimum));
      if (minus) minus.disabled = Number(qty.value) <= minimum;
      if (announce) qty.dispatchEvent(new Event('change', { bubbles: true }));
    }
    on(minus, 'click', () => setQty(Number(qty.value) - 1));
    on(root.querySelector('[data-qty-plus]'), 'click', () => setQty(Number(qty.value) + 1));
    on(qty, 'change', () => setQty(qty.value, false));
    if (qty) setQty(qty.value, false);

    const dialog = root.querySelector('[data-sq-zoom-dialog]');
    if (dialog?.showModal) {
      const image = dialog.querySelector('[data-sq-zoom-image]');
      const toggle = dialog.querySelector('[data-sq-zoom-toggle]');
      const close = dialog.querySelector('[data-sq-zoom-close]');
      const status = dialog.querySelector('[data-sq-zoom-status]');
      const original = dialog.querySelector('[data-sq-zoom-original]');
      const viewport = dialog.querySelector('.sq-zoom__viewport');
      let opener;
      const loaded = () => { if (status) status.hidden = true; image.hidden = false; toggle.disabled = false; viewport.setAttribute('aria-busy', 'false'); };
      on(image, 'load', loaded);
      on(image, 'error', () => {
        if (status) { status.textContent = dialog.dataset.errorLabel; status.hidden = false; }
        image.hidden = true; toggle.disabled = true; viewport.setAttribute('aria-busy', 'false');
      });
      root.querySelectorAll('[data-sq-zoom]').forEach(link => on(link, 'click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); opener = link;
        image.hidden = true; toggle.disabled = true;
        if (status) { status.textContent = dialog.dataset.loadingLabel; status.hidden = false; }
        if (original) original.href = link.href;
        viewport.setAttribute('aria-busy', 'true'); viewport.scrollTop = 0; viewport.scrollLeft = 0;
        image.alt = link.querySelector('img')?.alt || '';
        image.src = link.href;
        dialog.classList.remove('is-magnified'); toggle.setAttribute('aria-pressed', 'false');
        if (!dialog.open) dialog.showModal();
        close.focus({ preventScroll: true });
        if (image.complete && image.naturalWidth > 0) loaded();
      }));
      on(toggle, 'click', () => toggle.setAttribute('aria-pressed', String(dialog.classList.toggle('is-magnified'))));
      on(close, 'click', () => dialog.close());
      on(dialog, 'close', () => { if (opener?.isConnected) opener.focus({ preventScroll: true }); });
      on(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
    }

    const select = root.querySelector('[data-variant-select]');
    if (!select) return;
    const optionsUI = root.querySelector('[data-sq-options]');
    if (!optionsUI) return;
    const fieldsets = [...optionsUI.querySelectorAll('[data-sq-option-position]')];
    const variants = [...select.options].map(option => ({ option, values: JSON.parse(option.dataset.options) }));
    const buttons = [...root.querySelectorAll('[data-add-to-cart],[data-sticky-add]')];
    const stock = root.querySelector('[data-product-stock]');
    function setPrice(node, text) {
      if (!node) return;
      node.replaceChildren();
      if ((text || '').endsWith('د.أ')) {
        const wrap = document.createElement('span');
        const value = document.createElement('bdi');
        const currency = document.createElement('small');
        wrap.className = 'sq-money'; wrap.dir = 'ltr'; value.textContent = text.slice(0, -3).trim(); currency.textContent = 'د.أ';
        wrap.append(value, currency); node.append(wrap);
      } else node.textContent = text || '';
    }
    function optionStates() {
      const chosen = fieldsets.map(fieldset => fieldset.querySelector('input:checked')?.value);
      fieldsets.forEach((fieldset, position) => fieldset.querySelectorAll('input').forEach(input => {
        const label = input.closest('label');
        const hint = label.querySelector('[data-sq-option-state]');
        if (!input.dataset.baseLabel) input.dataset.baseLabel = label.querySelector('span').textContent.trim();
        const matches = variants.filter(variant => variant.values.every((value, index) => value === (index === position ? input.value : chosen[index])));
        const state = !matches.length ? 'unavailable' : matches.some(variant => variant.option.dataset.available === 'true') ? 'available' : 'soldout';
        const text = state === 'unavailable' ? root.dataset.unavailableLabel : state === 'soldout' ? root.dataset.soldoutLabel : '';
        label.dataset.availability = state;
        input.setAttribute('aria-label', `${input.dataset.baseLabel}${text ? ' — ' + text : ''}`);
        if (hint) { hint.textContent = text; hint.hidden = !text; }
      }));
    }
    function sync(changeURL = true) {
      const option = select.selectedOptions[0];
      const available = option?.dataset.available === 'true';
      form.dataset.available = String(available);
      buttons.forEach(button => {
        button.disabled = !available || form.dataset.busy === '1';
        if (form.dataset.busy !== '1') button.textContent = available ? root.dataset.addLabel : root.dataset.soldoutLabel;
      });
      stock.textContent = !option ? root.dataset.unavailableLabel : available ? root.dataset.availableLabel : root.dataset.soldoutLabel;
      stock.classList.toggle('is-soldout', !available);
      setPrice(root.querySelector('[data-product-price]'), option?.dataset.price || '—');
      setPrice(root.querySelector('[data-sticky-price]'), option?.dataset.price || '—');
      const compare = root.querySelector('[data-product-compare]');
      const discount = root.querySelector('[data-product-discount]');
      const saving = root.querySelector('[data-product-saving]');
      setPrice(compare, option?.dataset.compare); compare.hidden = !option?.dataset.compare;
      discount.textContent = (root.dataset.discountLabel || '').replace('[amount]', option?.dataset.discount || '0');
      discount.hidden = !(Number(option?.dataset.discount) > 0);
      saving.textContent = (root.dataset.savingLabel || '').replace('[amount]', option?.dataset.saving || '');
      saving.hidden = !option?.dataset.saving;
      if (changeURL) {
        const url = new URL(location.href);
        if (option) url.searchParams.set('variant', option.value); else url.searchParams.delete('variant');
        history.replaceState(history.state, '', url);
      }
      if (option) {
        if (changeURL && option.dataset.mediaId) {
          const index = slides.findIndex(slide => slide.dataset.mediaId === option.dataset.mediaId);
          if (index >= 0) showSlide(index);
        }
        const selected = JSON.parse(option.dataset.options);
        fieldsets.forEach((fieldset, index) => fieldset.querySelectorAll('input').forEach(input => { input.checked = input.value === selected[index]; }));
      }
      optionStates();
    }
    on(optionsUI, 'change', () => {
      const chosen = fieldsets.map(fieldset => fieldset.querySelector('input:checked')?.value);
      const match = variants.find(variant => variant.values.every((value, index) => value === chosen[index]));
      select.value = match?.option.value || '';
      sync();
      select.dispatchEvent(new CustomEvent('sooqja:variant:changed', { bubbles: true, detail: { variantId: match?.option.value || null } }));
    });
    on(select, 'change', () => sync());
    sync(false); optionsUI.hidden = false;
    root.querySelector('[data-sq-variant-fallback]').hidden = true;
  }
  document.querySelectorAll('[data-sooqja-product]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-sooqja-product]').forEach(init));
  const label = document.querySelector('[data-review-sort]')?.dataset.reviewSort;
  if (label) {
    const translate = node => {
      if (node.nodeType !== 1) return;
      const labels = node.matches('.jdgm-sort-dropdown-label') ? [node] : node.querySelectorAll('.jdgm-sort-dropdown-label');
      labels.forEach(element => { if (element.textContent !== label) element.textContent = label; });
    };
    translate(document.body);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(translate))).observe(document.body, { childList: true, subtree: true });
  }
})();
